import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { useCreateRole } from '@/admin/useAdminRoles';

const SLUG_PATTERN = /^[a-z0-9-]+$/;

export function CreateRoleForm() {
  const { t } = useTranslation();
  const create = useCreateRole();

  const [slug, setSlug] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setValidationError(null);

    if (!SLUG_PATTERN.test(slug)) {
      setValidationError(t('roles.errors.invalidSlug'));
      return;
    }

    create.mutate(
      { slug, name, description: description || null },
      {
        onSuccess: () => {
          setSlug('');
          setName('');
          setDescription('');
        },
      },
    );
  }

  const serverError = create.error;

  return (
    <form
      onSubmit={handleSubmit}
      aria-labelledby="roles-create-title"
      className="space-y-3 rounded-lg border bg-white p-4 shadow-sm"
    >
      <h3 id="roles-create-title" className="text-sm font-semibold text-appsol-900">
        {t('roles.create.title')}
      </h3>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="text-sm">
          <span className="block text-slate-600">{t('roles.fields.slug')}</span>
          <input
            required
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            placeholder="rrhh"
            className="mt-1 w-full rounded border px-2 py-1"
          />
        </label>
        <label className="text-sm">
          <span className="block text-slate-600">{t('roles.fields.name')}</span>
          <input
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Recursos Humanos"
            className="mt-1 w-full rounded border px-2 py-1"
          />
        </label>
        <label className="text-sm sm:col-span-2">
          <span className="block text-slate-600">{t('roles.fields.description')}</span>
          <input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="mt-1 w-full rounded border px-2 py-1"
          />
        </label>
      </div>

      {validationError && (
        <p role="alert" className="text-xs text-rose-700">
          {validationError}
        </p>
      )}
      {serverError && (
        <p role="alert" className="text-xs text-rose-700">
          {serverError.status === 409
            ? t('roles.errors.slugDuplicated')
            : t('roles.errors.createFailed')}
        </p>
      )}

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={create.isPending}
          className="rounded-md bg-appsol-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-appsol-900 disabled:opacity-50"
        >
          {create.isPending ? t('roles.create.submitting') : t('roles.create.submit')}
        </button>
      </div>
    </form>
  );
}
